import React, { useEffect, useState, useRef, useCallback } from "react";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Two-panel messaging UI (thread list left, chat right on desktop).
 * For clients: the left panel collapses to a single (auto-selected) thread.
 * Polls the server every 12s while the tab is focused.
 */
export default function Messages({ embedded = false }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeOtherId, setActiveOtherId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data.threads);
      // Auto-select first for clients (single thread) or if nothing selected
      if (!activeOtherId && data.threads.length > 0) {
        setActiveOtherId(data.threads[0].other_user.id);
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeOtherId]);

  const loadThread = useCallback(async (otherId) => {
    if (!otherId) return;
    try {
      const { data } = await api.get(`/messages/threads/${otherId}`);
      setThread(data);
      // Mark as read
      await api.post(`/messages/threads/${otherId}/read`).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load thread");
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (activeOtherId) loadThread(activeOtherId); }, [activeOtherId, loadThread]);

  // Poll every 12s while tab visible
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      loadThreads();
      if (activeOtherId) loadThread(activeOtherId);
    };
    const iv = setInterval(tick, 12000);
    return () => clearInterval(iv);
  }, [activeOtherId, loadThread, loadThreads]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeOtherId) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: activeOtherId, body: draft.trim() });
      setDraft("");
      await loadThread(activeOtherId);
      await loadThreads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-8 text-ink-lighter">Loading…</div>;

  const wrapperCls = embedded
    ? "card-surface overflow-hidden"
    : "p-6 lg:p-10 max-w-5xl mx-auto";

  return (
    <div className={wrapperCls} data-testid="messages-page">
      {!embedded && (
        <div className="mb-6">
          <div className="overline text-forest">Messages</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
            Client conversations
          </h1>
        </div>
      )}
      {threads.length === 0 ? (
        <EmptyState />
      ) : (
        <div className={`${embedded ? "" : "card-surface overflow-hidden"} flex flex-col md:flex-row min-h-[500px]`}>
          {/* Thread list — hidden for clients with a single thread */}
          {threads.length > 1 && (
            <div className="md:w-64 border-r border-border bg-paper/60 overflow-y-auto" data-testid="thread-list">
              {threads.map((t) => {
                const active = t.other_user.id === activeOtherId;
                return (
                  <button
                    key={t.other_user.id}
                    type="button"
                    onClick={() => setActiveOtherId(t.other_user.id)}
                    className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${
                      active ? "bg-white" : "hover:bg-white/60"
                    }`}
                    data-testid={`thread-${t.other_user.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">
                        {t.other_user.business_name || t.other_user.name}
                      </div>
                      {t.unread_count > 0 && (
                        <span
                          className="text-[10px] font-medium bg-forest text-white rounded-full px-1.5 py-0.5 shrink-0"
                          data-testid={`thread-${t.other_user.id}-unread`}
                        >
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-lighter truncate mt-0.5">
                      {t.last_message?.body || "No messages yet"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Chat panel */}
          <div className="flex-1 flex flex-col min-w-0" data-testid="chat-panel">
            {thread && (
              <>
                <div className="px-5 py-3 border-b border-border bg-white">
                  <div className="font-medium">
                    {thread.other_user.business_name || thread.other_user.name}
                  </div>
                  <div className="text-xs text-ink-lighter">
                    {thread.other_user.role === "client" ? "Client" : "Your accountant"}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-paper/40" data-testid="messages-scroll">
                  {thread.messages.length === 0 && (
                    <div className="text-center text-sm text-ink-lighter py-10">
                      Send the first message.
                    </div>
                  )}
                  {thread.messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div
                        key={m.id}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        data-testid={`message-${m.id}`}
                      >
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                          mine ? "bg-forest text-white" : "bg-white border border-border text-ink"
                        }`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-ink-lighter"}`}>
                            {fmtDateTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={send} className="px-4 py-3 border-t border-border bg-white flex gap-2" data-testid="message-form">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Write a message…"
                    className="flex-1 bg-white border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="btn-forest inline-flex items-center gap-1.5"
                    data-testid="message-send"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.75} />}
                    Send
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card-surface p-10 text-center" data-testid="messages-empty">
      <div className="w-12 h-12 mx-auto rounded-full bg-forest/10 text-forest flex items-center justify-center mb-3">
        <MessageSquare className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="font-heading text-lg font-medium">No conversations yet</div>
      <p className="text-sm text-ink-light mt-1 max-w-md mx-auto">
        Add a client on the Clients page — you&apos;ll be able to message them here as soon as they&apos;re invited.
      </p>
    </div>
  );
}
